import { toast } from 'react-toastify';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getTranslationKey } from 'helpers/texting';
import { commentImage } from 'store/slices/images/actions';
import {
  cancelSelectionAndDeleteIfUnsaved,
  commitSelectionEdits
} from 'store/slices/selection/actions';
import { saveSelection as saveSelectionAction } from 'modules/ScrAppBook/store/selectionWorkflow/actions';
import type { ISelection, IFavorite } from 'old-store/types/gallery';
import { setCookie } from 'helpers/cookie';

export interface ISelectionState {
  all: ISelection[];
  allUserSelections: ISelection[];
  selectionStarted: boolean;
  mobileSelectionMode: boolean;
  updateSelectionState: boolean;
  editSelectionModal: any;
  likes: string[];
  currentSelection: ISelection;
  editDraft: ISelectionEditDraft | null;
}

export interface ISelectionEditDraft {
  sourceSelectionId: string;
  imageIds: string[];
  originalImageIds: string[];
  previousLikes: string[];
  previousSelectionStarted: boolean;
  previousMobileSelectionMode: boolean;
  sourceWasCurrent: boolean;
  saveModalOpen: boolean;
  status: 'editing' | 'saving' | 'failed';
}

const initialState: ISelectionState = {
  all: [],
  allUserSelections: [],
  selectionStarted: false,
  mobileSelectionMode: false,
  updateSelectionState: false,
  editSelectionModal: {},
  likes: [],
  editDraft: null,
  currentSelection: {
    __v: 0,
    _collection: '',
    _endCustomer: '',
    _id: '',
    _user: '',
    name: '',
    createdAt: '',
    updatedAt: '',
    completed: false,
    hasImagesFromHiddenGalleries: false,
    favorites: []
  }
};

const selectionSlice = createSlice({
  name: 'selection',
  initialState,
  reducers: {
    saveSelection(
      state,
      { payload }: PayloadAction<{ selections: ISelection[]; allUserSelections: ISelection[] }>
    ) {
      state.all = payload.selections;

      const currentSelection = payload.selections.find(
        (selection: ISelection) => selection.current
      );

      if (currentSelection) {
        state.currentSelection = currentSelection;
        const likedArray = currentSelection.favorites
          .filter((el: IFavorite) => el.like)
          .map((el: IFavorite) => el._image);

        state.selectionStarted =
          Boolean(likedArray.length) || !!currentSelection.hasImagesFromHiddenGalleries;
        setCookie('selectionStarted', String(state.selectionStarted));

        state.mobileSelectionMode = payload.selections.some((item: ISelection) => item.current);
        state.likes = likedArray;
      }

      if (payload?.allUserSelections?.length) {
        state.allUserSelections = payload.allUserSelections;
      }

      // Customer refreshes still update the available selections, but an in-progress edit stays
      // entirely local until one of the explicit commit actions succeeds.
      if (state.editDraft) {
        state.likes = state.editDraft.imageIds;
        state.selectionStarted = true;
        state.mobileSelectionMode = true;
        setCookie('selectionStarted', 'true');
      }
    },
    handleLikes(state, { payload }: PayloadAction<string>) {
      state.selectionStarted = true;
      setCookie('selectionStarted', 'true');

      state.mobileSelectionMode = true;

      if (state.likes.includes(payload)) {
        state.likes = state.likes.filter((item) => item !== payload);
      } else {
        state.likes.push(payload);
      }
    },
    // Bulk local like/unlike for the new-design select-all / deselect-all (backend synced in one
    // request by the thunks). addLikes mirrors handleLikes' selection-mode side effects.
    addLikes(state, { payload }: PayloadAction<string[]>) {
      state.selectionStarted = true;
      setCookie('selectionStarted', 'true');
      state.mobileSelectionMode = true;

      const merged = new Set(state.likes);
      payload.forEach((id) => merged.add(id));
      state.likes = Array.from(merged);
    },
    removeLikes(state, { payload }: PayloadAction<string[]>) {
      const toRemove = new Set(payload);
      state.likes = state.likes.filter((id) => !toRemove.has(id));
    },
    addSelection(state, { payload }) {
      if (payload) state.all.push(payload);
      if (payload?.current) state.currentSelection = payload;
    },
    startSelection: (state) => {
      state.selectionStarted = true;
      state.mobileSelectionMode = true;
      setCookie('selectionStarted', 'true');
    },
    finishSelection(state) {
      state.selectionStarted = false;
      setCookie('selectionStarted', 'false');

      state.mobileSelectionMode = false;
      state.updateSelectionState = false;
      state.likes = [];
      state.all = state.all.map((item) => ({ ...item, current: false }));
      state.currentSelection = initialState.currentSelection;
    },
    enableMobileSelectionMode(state) {
      state.mobileSelectionMode = true;
    },
    disableMobileSelectionMode(state) {
      state.mobileSelectionMode = false;
    },
    enableUpdateSelectionState(state) {
      state.updateSelectionState = true;
    },
    disableUpdateSelectionState(state) {
      state.updateSelectionState = false;
    },
    beginSelectionEdit(
      state,
      {
        payload
      }: PayloadAction<{
        sourceSelectionId: string;
        imageIds: string[];
      }>
    ) {
      const imageIds = Array.from(new Set(payload.imageIds));

      state.editDraft = {
        sourceSelectionId: payload.sourceSelectionId,
        imageIds,
        originalImageIds: [...imageIds],
        previousLikes: [...state.likes],
        previousSelectionStarted: state.selectionStarted,
        previousMobileSelectionMode: state.mobileSelectionMode,
        sourceWasCurrent: state.all.some(
          (selection) => selection._id === payload.sourceSelectionId && selection.current
        ),
        saveModalOpen: false,
        status: 'editing'
      };
      state.likes = imageIds;
      state.selectionStarted = true;
      state.mobileSelectionMode = true;
      setCookie('selectionStarted', 'true');
    },
    toggleSelectionEditImage(state, { payload }: PayloadAction<string>) {
      if (!state.editDraft) return;

      if (state.editDraft.imageIds.includes(payload)) {
        state.editDraft.imageIds = state.editDraft.imageIds.filter((id) => id !== payload);
      } else {
        state.editDraft.imageIds.push(payload);
      }

      state.likes = state.editDraft.imageIds;
      state.editDraft.status = 'editing';
    },
    addSelectionEditImages(state, { payload }: PayloadAction<string[]>) {
      if (!state.editDraft) return;

      state.editDraft.imageIds = Array.from(new Set([...state.editDraft.imageIds, ...payload]));
      state.likes = state.editDraft.imageIds;
      state.editDraft.status = 'editing';
    },
    removeSelectionEditImages(state, { payload }: PayloadAction<string[]>) {
      if (!state.editDraft) return;

      const toRemove = new Set(payload);
      state.editDraft.imageIds = state.editDraft.imageIds.filter((id) => !toRemove.has(id));
      state.likes = state.editDraft.imageIds;
      state.editDraft.status = 'editing';
    },
    openSelectionEditSaveModal(state) {
      if (!state.editDraft) return;

      state.editDraft.saveModalOpen = true;
      state.editDraft.status = 'editing';
    },
    closeSelectionEditSaveModal(state) {
      if (!state.editDraft || state.editDraft.status === 'saving') return;

      state.editDraft.saveModalOpen = false;
      state.editDraft.status = 'editing';
    },
    discardSelectionEdit(state) {
      if (!state.editDraft) return;

      state.likes = state.editDraft.previousLikes;
      state.selectionStarted = state.editDraft.previousSelectionStarted;
      state.mobileSelectionMode = state.editDraft.previousMobileSelectionMode;
      setCookie('selectionStarted', String(state.selectionStarted));
      state.editDraft = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveSelectionAction.fulfilled, (state, { payload }) => {
        if (!payload?.currentSelection) {
          toast.error(getTranslationKey('errorMessage'), { theme: 'colored' });

          return;
        }

        state.selectionStarted = false;
        setCookie('selectionStarted', 'false');

        state.mobileSelectionMode = false;
        state.updateSelectionState = false;
        state.likes = [];
        state.all = state.all.map((item) => ({ ...item, current: false }));
        state.currentSelection = initialState.currentSelection;
      })
      .addCase(cancelSelectionAndDeleteIfUnsaved.fulfilled, (state) => {
        state.selectionStarted = false;
        setCookie('selectionStarted', 'false');

        state.mobileSelectionMode = false;
        state.updateSelectionState = false;
        state.likes = [];
        state.currentSelection = initialState.currentSelection;
      })
      .addCase(commitSelectionEdits.pending, (state) => {
        if (!state.editDraft) return;

        state.editDraft.status = 'saving';
      })
      .addCase(commitSelectionEdits.rejected, (state) => {
        if (!state.editDraft) return;

        state.editDraft.status = 'failed';
        state.editDraft.saveModalOpen = true;
      })
      .addCase(commitSelectionEdits.fulfilled, (state, { payload }) => {
        if (!state.editDraft) return;

        const { sourceSelectionId } = state.editDraft;
        const existingIndex = state.all.findIndex((selection) => selection._id === payload._id);

        if (existingIndex >= 0) state.all[existingIndex] = payload;
        else state.all.push(payload);

        if (payload._id === sourceSelectionId) {
          state.all = state.all.map((selection) =>
            selection._id === sourceSelectionId ? payload : selection
          );
        }

        const sourceWasOverwritten =
          payload._id === sourceSelectionId && state.editDraft.sourceWasCurrent;

        state.likes = sourceWasOverwritten
          ? payload.favorites.filter((favorite) => favorite.like).map((favorite) => favorite._image)
          : state.editDraft.previousLikes;
        state.selectionStarted = sourceWasOverwritten
          ? state.likes.length > 0 || !!payload.hasImagesFromHiddenGalleries
          : state.editDraft.previousSelectionStarted;
        state.mobileSelectionMode = state.editDraft.previousMobileSelectionMode;
        if (sourceWasOverwritten) state.currentSelection = payload;
        setCookie('selectionStarted', String(state.selectionStarted));
        state.editDraft = null;
      })
      .addCase(commentImage.fulfilled, (_, { payload }) => {
        // @ts-ignore
        if (payload._id) {
          toast.success(getTranslationKey('commentSaved'), {
            theme: 'colored',
            className: 'toast-custom-top-position'
          });
        }
      });
  }
});

export const {
  saveSelection,
  handleLikes,
  addLikes,
  removeLikes,
  startSelection,
  finishSelection,
  enableMobileSelectionMode,
  disableMobileSelectionMode,
  enableUpdateSelectionState,
  disableUpdateSelectionState,
  addSelection,
  beginSelectionEdit,
  toggleSelectionEditImage,
  addSelectionEditImages,
  removeSelectionEditImages,
  openSelectionEditSaveModal,
  closeSelectionEditSaveModal,
  discardSelectionEdit
} = selectionSlice.actions;

export const selectionReducer = selectionSlice.reducer;
